import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatDate, formatCOP, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Disclaimer } from "../components/Disclaimer";
import StateTransitionPanel from "../components/StateTransitionPanel";
import { toast } from "sonner";

const STATE_LABELS = {
  draft_intake: "Borrador",
  nivel0_pending: "Explorador pendiente",
  nivel0_ready: "Explorador listo",
  nivel1_payment_pending: "Pendiente pago Diagnóstico",
  nivel1_paid: "Pago Diagnóstico confirmado",
  nivel1_processing: "Procesando Diagnóstico",
  nivel1_needs_human_review: "Revisión humana",
  nivel1_revision_requested: "Revisión solicitada",
  nivel1_delivered: "Diagnóstico entregado",
  nivel2_offer_sent: "Oferta Kit Documental enviada",
  nivel2_payment_pending: "Pendiente pago Kit Documental",
  out_of_scope: "Fuera de alcance",
  closed: "Cerrado",
};

function Pill({ children, tone = "default" }) {
  const tones = {
    default: "bg-gray-100 text-gray-800 border-gray-300",
    ok: "bg-emerald-50 text-emerald-900 border-emerald-300",
    warn: "bg-amber-50 text-amber-900 border-amber-300",
    err: "bg-rose-50 text-rose-900 border-rose-300",
    info: "bg-sky-50 text-sky-900 border-sky-300",
  };
  return <span className={`inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider ${tones[tone] || tones.default}`}>{children}</span>;
}

function KV({ label, value, mono = false, testid }) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-widest text-gray-500">{label}</span>
      <span className={mono ? "text-xs font-mono text-gray-800 break-all" : "text-sm text-gray-900"}>{value ?? "—"}</span>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div className="border border-gray-200 bg-white p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg text-gray-900">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function AdminCaseUnifiedPage() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [payments, setPayments] = useState([]);
  const [prev, setPrev] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("summary");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a, p, rev] = await Promise.all([
        api.get(`/admin/cases?limit=1&state=`),
        api.get(`/admin/cases/${id}/audit?limit=50`).catch(() => ({ data: { items: [] } })),
        api.get(`/admin/cases/${id}/payments`).catch(() => ({ data: { items: [] } })),
        api.get(`/admin/reviewers`).catch(() => ({ data: [] })),
      ]);
      // c.data.items not filtered by id; fetch individual
      const det = await api.get(`/admin/cases?limit=200`);
      const found = (det.data?.items || []).find((x) => x.id === id);
      setCaseData(found || null);
      setAudit(a.data?.items || []);
      setPayments(p.data?.items || []);
      setReviewers(rev.data || []);
      // Prevalidation
      try {
        const pr = await api.get(`/admin/cases/${id}/prevalidation-result`);
        setPrev(pr.data);
      } catch { setPrev(null); }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el caso");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const assignReviewer = async (rid) => {
    try {
      await api.post(`/admin/cases/${id}/assign`, { reviewer_id: rid || null });
      toast.success(rid ? "Revisor asignado" : "Revisor desasignado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo asignar");
    }
  };

  if (loading) return <div className="container-prose py-10 text-gray-500">Cargando caso…</div>;
  if (!caseData) return <div className="container-prose py-10"><Link to="/admin" className="text-primary underline">← Admin</Link><p className="mt-4 text-rose-700">Caso no encontrado.</p></div>;

  const c = caseData;
  return (
    <div className="container-prose py-8 max-w-6xl" data-testid="admin-case-unified">
      <div className="flex items-center justify-between mb-4">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary">← Admin</Link>
        <code className="text-xs text-gray-500 font-mono">{c.id}</code>
      </div>
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Vista unificada · admin</p>
      <h1 className="font-serif text-3xl text-gray-900 mb-2">{c.title}</h1>
      <div className="flex flex-wrap gap-2 mb-4">
        <Pill tone="info">{STATE_LABELS[c.state] || c.state}</Pill>
        {c.payment_status && <Pill>{c.payment_status}</Pill>}
        {c.review_status && <Pill>{c.review_status}</Pill>}
        {c.last_generation_error && <Pill tone="err">Error generación</Pill>}
      </div>

      {/* Transiciones contextuales por estado del caso */}
      <div className="border border-gray-200 bg-gray-50 rounded-sm p-4 mb-6">
        <StateTransitionPanel
          caseId={id}
          currentState={c.state}
          onTransitioned={() => load()}
          endpoint="/admin/cases"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex flex-wrap" data-testid="admin-case-tabs">
          <TabsTrigger value="summary" data-testid="tab-summary">Resumen</TabsTrigger>
          <TabsTrigger value="prevalidation" data-testid="tab-prevalidation">Prevalidación</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Pagos</TabsTrigger>
          <TabsTrigger value="secop" data-testid="tab-secop">Comparables</TabsTrigger>
          <TabsTrigger value="diagnosis" data-testid="tab-diagnosis">Diagnóstico</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">Revisión</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
          <TabsTrigger value="reviewer" data-testid="tab-reviewer">Revisor</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <Section title="Datos del caso">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <KV label="Título" value={c.title} testid="case-title" />
              <KV label="Estado" value={STATE_LABELS[c.state] || c.state} />
              <KV label="Owner" value={`${c.user_name || "—"} (${c.user_email || "—"})`} />
              <KV label="Valor estimado" value={c.valor_estimado_cop ? formatCOP(c.valor_estimado_cop) : "—"} />
              <KV label="Creado" value={formatDate(c.created_at)} />
              <KV label="Actualizado" value={formatDate(c.updated_at)} />
              <KV label="Revisor asignado" value={c.assigned_reviewer_email || "Sin asignar"} />
              <KV label="seed_origin" value={c.seed_origin || "—"} mono />
            </div>
            {c.last_generation_error && (
              <div className="mt-4 border border-rose-300 bg-rose-50 p-3 text-sm">
                <p className="text-rose-900 font-medium mb-1">last_generation_error</p>
                <p className="text-rose-800 font-mono text-xs whitespace-pre-wrap">{c.last_generation_error}</p>
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="prevalidation">
          <Section title="Resultado Explorador">
            {prev ? (
              <div>
                <KV label="Semáforo" value={prev.semaforo || prev.resultado?.semaforo || "—"} />
                <KV label="Razón" value={prev.razon || prev.resultado?.razon || "—"} />
                <details className="mt-3"><summary className="text-xs text-gray-600 cursor-pointer">Ver JSON completo</summary>
                  <pre className="text-[11px] bg-gray-50 border border-gray-200 p-3 mt-2 overflow-x-auto">{JSON.stringify(prev, null, 2)}</pre>
                </details>
              </div>
            ) : <p className="text-sm text-gray-500 italic">Sin prevalidación registrada todavía.</p>}
          </Section>
        </TabsContent>

        <TabsContent value="payments">
          <Section title="Pagos del caso" action={<Link to={`/admin#payments`} className="text-xs text-primary underline">Ir a bandeja</Link>}>
            {payments.length === 0 ? <p className="text-sm text-gray-500 italic">Sin pagos registrados.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 border-b">
                  <th className="text-left py-2">Ref</th><th className="text-left">Nivel</th><th className="text-right">Monto</th><th className="text-left">Status</th><th className="text-right">Creado</th>
                </tr></thead>
                <tbody>{payments.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100" data-testid={`pay-row-${p.id}`}>
                    <td className="py-2 font-mono text-xs">{p.reference || p.referencia || "—"}</td>
                    <td>{p.nivel}</td>
                    <td className="text-right">{formatCOP(p.amount_cop || p.monto_cop)}</td>
                    <td><Pill tone={p.status === "confirmed" ? "ok" : p.status === "pending" ? "warn" : "default"}>{p.status}</Pill></td>
                    <td className="text-right text-xs text-gray-500">{formatDate(p.created_at)}</td>
                  </tr>))}
                </tbody>
              </table>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="secop">
          <Section title="Comparables SECOP" action={<Link to={`/admin/cases/${id}/secop`} className="text-xs text-primary underline">Abrir gestor completo →</Link>}>
            <p className="text-sm text-gray-600">La gestión completa (buscar, seleccionar, agregar manual, aceptar limitación) vive en la página dedicada.</p>
          </Section>
        </TabsContent>

        <TabsContent value="diagnosis">
          <Section title="Diagnóstico generado" action={<Link to={`/admin/cases/${id}/diagnosis`} className="text-xs text-primary underline">Abrir viewer →</Link>}>
            <p className="text-sm text-gray-600">Viewer, versiones, regenerar y diff de versiones disponibles en la página dedicada.</p>
          </Section>
        </TabsContent>

        <TabsContent value="review">
          <Section title="Estado de revisión">
            <KV label="review_status" value={c.review_status || "—"} />
            <KV label="Revisor" value={c.assigned_reviewer_email || "Sin asignar"} />
            <Link to={`/admin/cases/${id}/diagnosis`} className="text-xs text-primary underline">Ver checklist + acciones del reviewer →</Link>
          </Section>
        </TabsContent>

        <TabsContent value="audit">
          <Section title="Audit log del caso">
            {audit.length === 0 ? <p className="text-sm text-gray-500 italic">Sin eventos.</p> : (
              <ol className="border-l border-gray-200 ml-2 space-y-3">
                {audit.map((e) => (
                  <li key={e.id} className="pl-4 relative" data-testid={`audit-${e.id}`}>
                    <span className="absolute -left-1.5 top-1.5 w-2 h-2 bg-gray-400 rounded-full" />
                    <div className="text-xs text-gray-500">{formatDate(e.created_at)} · <code className="font-mono">{e.action}</code></div>
                    {e.metadata && <pre className="text-[11px] text-gray-700 bg-gray-50 border border-gray-200 p-2 mt-1 overflow-x-auto">{JSON.stringify(e.metadata, null, 2)}</pre>}
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="reviewer">
          <Section title="Asignación de revisor">
            <p className="text-sm text-gray-600 mb-3">Actual: <strong>{c.assigned_reviewer_email || "Sin asignar"}</strong></p>
            <div className="flex flex-wrap gap-2">
              {reviewers.map((r) => (
                <Button key={r.id} variant={r.id === c.assigned_reviewer_id ? "default" : "outline"} className="rounded-sm" onClick={() => assignReviewer(r.id)} data-testid={`assign-${r.id}`}>
                  {r.email}
                </Button>
              ))}
              {c.assigned_reviewer_id && (
                <Button variant="outline" className="rounded-sm border-rose-300 text-rose-700" onClick={() => assignReviewer(null)} data-testid="unassign-reviewer">Desasignar</Button>
              )}
            </div>
          </Section>
        </TabsContent>
      </Tabs>

      <Disclaimer />
    </div>
  );
}
